import router from '@/router'
import { createApp } from 'vue'
import { store } from '@/store'
import { createMetaManager } from 'vue-meta'
import * as Sentry from '@sentry/vue'

import App from '@/App.vue'

import { useSettings } from '@/store/settings'
import { useCountries } from '@/store/countries'
import { usePreferences } from '@/store/preferences'

import { useThemes } from '@/libs/composables/themes'
import { useFingerprint } from '@/store/fingerprint'

import clickOutside from 'click-outside-vue3'
import { vAutosize, vTooltip, vMaska } from '@/libs/directives'
import { i18n } from '@/libs/i18n'
import 'moment/dist/locale/lv'

const metaManager = createMetaManager()

const app = createApp(App)

app.use(router)
app.use(store)
app.use(i18n)
app.use(metaManager)

app.directive('click-outside', clickOutside.directive)
app.directive('autosize', vAutosize)
app.directive('tooltip', vTooltip)
app.directive('maska', vMaska)

Promise.all([
  useThemes().init(),
  useCountries().init(),
  useSettings().getSettings(),
  useFingerprint().getData(),
]).then(() => {
  const preferences = usePreferences()
  if (preferences.locale && ['en', 'lv'].includes(preferences.locale)) {
    preferences.setLocale(preferences.locale)
  }
  preferences.initCrossTabSync()

  Sentry.init({
    app,
    enabled: process.env.NODE_ENV !== 'development',
    environment: process.env.NODE_ENV,
    dsn: 'https://68dc39fc7c4be5236aaea9457310bd25@sentry.finsei.com/4',
    sendDefaultPii: true,

    ignoreErrors: ['Request failed with status code 400'],

    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,

    // // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    // tracePropagationTargets: ['localhost', 'https://app.finsei.com'],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })

  app.mount('#app')
})

/** Reload on missing dynamic chunk (after a new deployment). */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  window.location.reload()
})
